import React, { useMemo, Fragment, useState } from "react";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import { styled } from "@mui/material/styles";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import Divider from "@mui/material/Divider";

// Timeline context
// import { useTimeline } from "cxfcomponents/Timeline/context";
import { useTimeline } from "../context";

// Custom styles for the TimelineItem
// import { timelineItem, timelineItemIcon } from "cxfcomponents/Timeline/TimelineItem/styles";
import { timelineItem, timelineItemIcon } from "./styles";
import capitalizeFirstLetter from "helpers/capitalizeFirstLetter";
import {
	isArrayofObjects,
	stringifyWithSpaces,
	isValidJsonString
} from "helpers/common";

import { TimelineEditor } from "./Editor";
import CopyButton from "./CopyButton";
import { WrapButton } from "./WrapButton";

const CopyButtonWrapper = styled(SoftBox)(() => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between"
}));

const ResponseValueLabel = ({ label }) => {
	return (
		<SoftTypography
			variant="button"
			sx={{ whiteSpace: "normal" }}
			fontWeight="regular"
			color="text"
		>
			{label || "will be assigned"}&nbsp;
		</SoftTypography>
	);
};

function TimelineItem({
	color = "info",
	nodeId = "",
	icon,
	title,
	dateTime,
	description,
	badges = [],
	undefinedPaths = [],
	lastItem = false,
	params,
	response,
	isActionEnd,
	componentKey
}) {
	const isDark = useTimeline();
	const [isWrapped, setIsWrapped] = useState(false);

	const renderBadges = useMemo(
		() =>
			badges.map(({ content, color }, key) => (
				<SoftBox
					key={`badge-${key}`}
					mr={key === badges.length - 1 ? 0 : 0.5}
				>
					<SoftBadge
						color={color}
						size="xs"
						badgeContent={content}
						container
					/>
				</SoftBox>
			)),
		[badges, color]
	);

	const renderValue = (value) => {
		let label = "";

		if (Array.isArray(value)) {
			if (isArrayofObjects(value)) {
				return value.map((val, index) => {
					return (
						<SoftBox
							key={index}
							ml={3}
						>
							{renderValue(val)}
						</SoftBox>
					);
				});
			} else {
				label = value.join(", ");

				return <ResponseValueLabel label={label} />;
			}
		}

		if (!!value && typeof value === "object") {
			return Object.entries(value).map(([key, val]) => {
				label = `${capitalizeFirstLetter(key)}:`;
				return (
					<SoftBox
						key={key}
						ml={3}
					>
						<ResponseValueLabel label={label} />
						{key === "Authorization" || key === "password"
							? "************"
							: renderValue(val)}
					</SoftBox>
				);
			});
		}

		label = value;

		if (typeof label === "boolean") {
			label = label.toString();
		}
		return <ResponseValueLabel label={label} />;
	};

	const multiBranchResult = (apiResults) => {
		let result = {
			input: apiResults.input_value ?? "",
			operator: apiResults.operator ?? "",
			value_to_compare_with: apiResults.value_to_compare_with ?? "",
			result: {}
		};
		if (Object.prototype.hasOwnProperty.call(apiResults, "selection_branch_result")) {
			return {
				...result,
				result: apiResults.selection_branch_result
			};
		} else if (Object.prototype.hasOwnProperty.call(apiResults, "branches")) {
			const resultBranch = Object.keys(apiResults.branches).filter(
				(key) => apiResults.branches[key].result
			);

			return {
				...result,
				result: resultBranch.length > 0 ? resultBranch[0] : ""
			};
		} else {
			return apiResults;
		}
	};

	const handleWrapResult = (e) => {
		e.stopPropagation();

		setIsWrapped((prev) => !prev);
	};

	const renderResponseResult = (apiResult) => {
		let apiResults = apiResult;

		if (apiResult !== null && typeof apiResult === "object") {
			apiResults = Object.fromEntries(
				Object.entries(apiResult).filter(([key]) => key !== "apiConfig")
			);
			apiResults = multiBranchResult(apiResults);
		}

		if (apiResult !== undefined && apiResult !== null) {
			const showEditor = isValidJsonString(
				typeof apiResult === "string"
					? apiResult
					: stringifyWithSpaces(apiResults)
			);

			return (
				<>
					<SoftBox>
						<CopyButtonWrapper>
							<SoftTypography
								variant="button"
								fontWeight="medium"
								textTransform="capitalize"
							>
								Result:{" "}
							</SoftTypography>
							<div style={{ display: "flex", alignItems: "center" }}>
								<WrapButton
									isWrapped={isWrapped}
									handleWrapResult={handleWrapResult}
								/>
								<CopyButton value={stringifyWithSpaces(apiResults)} />
							</div>
						</CopyButtonWrapper>

						{showEditor ? (
							<div style={{ marginBottom: "15px" }}>
								<TimelineEditor
									defaultValue={stringifyWithSpaces(apiResults)}
									isWrapped={isWrapped}
								/>
							</div>
						) : (
							<SoftTypography
								variant="button"
								fontWeight="regular"
								color="text"
								pl={2}
							>
								{typeof apiResult === "string"
									? apiResult
									: stringifyWithSpaces(apiResults)}
							</SoftTypography>
						)}
					</SoftBox>
				</>
			);
		}
	};

	return (
		<SoftBox
			className={`timeline-log-${nodeId}`}
			position="relative"
			sx={(theme) => timelineItem(theme, { lastItem, isActionEnd })}
		>
			<SoftBox
				bgColor={isDark ? "dark" : "white"}
				className="timeline-item-icon"
				width="1.625rem"
				height="1.625rem"
				borderRadius="50%"
				position="absolute"
				top="0"
				left="2px"
				zIndex={2}
			>
				<Icon sx={(theme) => timelineItemIcon(theme, { color })}>
					{icon}
				</Icon>
			</SoftBox>
			<SoftBox
				ml={5.75}
				pt={description ? 0.7 : 0.5}
				lineHeight={0}
			>
				<SoftTypography
					variant="button"
					fontWeight="medium"
					color={isDark ? "white" : "dark"}
				>
					{title}
				</SoftTypography>
				<SoftBox mt={0.5}>
					<SoftTypography
						variant="caption"
						fontWeight="medium"
						color={isDark ? "secondary" : "text"}
					>
						{dateTime}
					</SoftTypography>
				</SoftBox>
				{description && (
					<SoftBox
						mt={2}
						mb={1.5}
					>
						{description}
					</SoftBox>
				)}
				{params && (
					<>
						<SoftBox
							mt={2}
							mb={1.5}
						>
							{Object.entries(params).map(([key, value]) => {
								const showEditor = isValidJsonString(value);
								if (Array.isArray(value) && value.length === 0)
									return <Fragment key={key} />;

								if (
									Array.isArray(value) &&
									!value.every((item) => typeof item === "string")
								) {
									value = value.map(({ path, ...rest }) => rest);
								}

								return (
									<SoftBox key={key}>
										{showEditor ? (
											<div style={{ marginBottom: "15px" }}>
												<CopyButtonWrapper>
													<SoftTypography
														variant="button"
														fontWeight="regular"
														color="text"
													>
														{key.toFlatText()}: &nbsp;
													</SoftTypography>
													<CopyButton value={value} />
												</CopyButtonWrapper>

												<TimelineEditor defaultValue={value} />
											</div>
										) : (
											<React.Fragment>
												<SoftTypography
													variant="button"
													fontWeight="regular"
													color="text"
												>
													{key.toFlatText()}: &nbsp;
												</SoftTypography>
												{renderValue(value)}
											</React.Fragment>
										)}
									</SoftBox>
								);
							})}
						</SoftBox>
					</>
				)}
				{undefinedPaths?.length > 0 && (
					<SoftBox>
						<div style={{ marginBottom: "15px" }}>
							<SoftTypography
								variant="button"
								fontWeight="regular"
								color="text"
							>
								Undefined paths: &nbsp;
							</SoftTypography>
							<TimelineEditor
								defaultValue={JSON.stringify(undefinedPaths, null, 2)}
							/>
						</div>
					</SoftBox>
				)}
				{response !== undefined && response !== null && renderResponseResult(response)}
				{badges && badges.length > 0 && (
					<SoftBox
						display="flex"
						pb={lastItem ? 1 : 2}
					>
						{renderBadges}
					</SoftBox>
				)}
				{componentKey?.toLowerCase().includes("trigger") && <Divider />}
				{isActionEnd && !lastItem && <Divider />}
			</SoftBox>
		</SoftBox>
	);
}

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
	color: PropTypes.oneOf([
		"primary",
		"secondary",
		"info",
		"success",
		"warning",
		"error",
		"dark",
		"light"
	]),
	icon: PropTypes.node.isRequired,
	title: PropTypes.string,
	dateTime: PropTypes.string.isRequired,
	badges: PropTypes.arrayOf(
		PropTypes.oneOfType([PropTypes.string, PropTypes.number])
	),
	lastItem: PropTypes.bool
};

export default TimelineItem;
